import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Table, Tag, Typography, Flex, Empty, Spin, Popover, Checkbox, Button, message } from "antd";
import { SwapOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import orderTagService from "../../services/orderTagService";

const { Title, Text } = Typography;

const TaggedOrders = () => {
  const { uid } = useParams();
  const [orders, setOrders] = useState([]);
  const [tagInfo, setTagInfo] = useState(null);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const fetchOrders = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const [ordersRes, tagsRes] = await Promise.all([
        orderTagService.getOrdersByTag(uid, page, 20),
        orderTagService.getTags(),
      ]);
      if (ordersRes?.success) {
        setOrders(ordersRes.data?.results || []);
        setPagination((p) => ({ ...p, total: ordersRes.data?.count || 0 }));
        if (ordersRes.data?.tag) setTagInfo(ordersRes.data.tag);
      }
      if (tagsRes?.success) setAllTags(tagsRes.data || []);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    fetchOrders(pagination.current);
  }, [uid]);

  // Refresh tag list when tags are created/edited/deleted from sidebar
  useEffect(() => {
    const handler = async () => {
      const res = await orderTagService.getTags();
      if (res?.success) setAllTags(res.data || []);
    };
    window.addEventListener("tag-list-changed", handler);
    return () => window.removeEventListener("tag-list-changed", handler);
  }, []);

  const handleRemove = async (taskKey) => {
    await orderTagService.removeTag(uid, taskKey);
    message.success("Removed from tag");
    window.dispatchEvent(new Event("tag-assignment-changed"));
    fetchOrders(pagination.current);
  };

  const MoveTagPopover = ({ taskKey, currentUid }) => {
    const [open, setOpen] = useState(false);
    const otherTags = allTags.filter((t) => t.uid !== currentUid);

    const handleMove = async (targetUid) => {
      await orderTagService.removeTag(currentUid, taskKey);
      await orderTagService.assignTag(targetUid, taskKey);
      message.success("Moved to new tag");
      setOpen(false);
      window.dispatchEvent(new Event("tag-assignment-changed"));
      fetchOrders(pagination.current);
    };

    return (
      <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        placement="left"
        title="Move to tag"
        content={
          <div style={{ minWidth: 160 }}>
            {otherTags.length === 0 ? (
              <Text type="secondary">No other tags</Text>
            ) : (
              otherTags.map((t) => (
                <Flex
                  key={t.uid}
                  align="center"
                  gap={8}
                  style={{ padding: "5px 0", cursor: "pointer" }}
                  onClick={() => handleMove(t.uid)}
                >
                  <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: t.color, display: "inline-block" }} />
                  <Text style={{ fontSize: 13 }}>{t.name}</Text>
                </Flex>
              ))
            )}
          </div>
        }
      >
        <Button size="small" icon={<SwapOutlined />} title="Move to different tag" />
      </Popover>
    );
  };

  const columns = [
    {
      title: "Order ID",
      dataIndex: "key",
      render: (key) => (
        <Link to={`/order-management/my-orders/${key}`} target="_blank">
          {key}
        </Link>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (text) => (text?.length > 40 ? `${text.substring(0, 40)}...` : text),
    },
    { title: "Word Count", dataIndex: "word" },
    { title: "Status", dataIndex: "status" },
    {
      title: "Deadline",
      dataIndex: "deadline_hard",
      render: (d) => (d ? moment(d).local().format("DD-MM-YYYY hh:mm A") : "-"),
    },
    {
      title: "Tags",
      dataIndex: "tags",
      render: (tags) =>
        tags?.map((t) => (
          <Tag key={t.uid} color={t.color} style={{ marginBottom: 4 }}>
            {t.name}
          </Tag>
        )),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Flex gap={6}>
          <MoveTagPopover taskKey={record.key} currentUid={uid} />
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            title="Remove from this tag"
            onClick={() => handleRemove(record.key)}
          />
        </Flex>
      ),
    },
  ];

  return (
    <Flex vertical style={{ padding: 16 }} gap={16}>
      <Flex align="center" gap={12}>
        {tagInfo && (
          <span
            style={{
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: tagInfo.color,
              display: "inline-block",
            }}
          />
        )}
        <Title level={4} style={{ margin: 0 }}>
          {tagInfo ? `Orders tagged: ${tagInfo.name}` : "Tagged Orders"}
        </Title>
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" style={{ height: "50vh" }}>
          <Spin size="large" />
        </Flex>
      ) : orders.length === 0 ? (
        <Empty description="No orders with this tag" />
      ) : (
        <Table
          dataSource={orders}
          columns={columns}
          rowKey="key"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            onChange: (page) => {
              setPagination((p) => ({ ...p, current: page }));
              fetchOrders(page);
            },
          }}
          scroll={{ x: "max-content" }}
        />
      )}
    </Flex>
  );
};

export default TaggedOrders;
